import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { exportFilename } from "@/lib/timefmt";
import { Plus, Trash2, Pencil, Copy, ChevronDown, ChevronRight, Download, Upload } from "lucide-react";

const emptyType = { name: "", kind: "task", brief_min: 0, debrief_min: 0, role_ids: [], color: "#8b5cf6", fixed: false, requires_authorisation: false, auth_scope: "unit", airspaces_text: "", equipment_types: [] };

const PALETTE256 = (() => {
  const cols = [];
  const hex = (v) => v.toString(16).padStart(2, "0");
  for (const r of [0, 51, 102, 153, 204, 255])
    for (const g of [0, 51, 102, 153, 204, 255])
      for (const b of [0, 51, 102, 153, 204, 255])
        cols.push(`#${hex(r)}${hex(g)}${hex(b)}`);
  for (let i = 0; i < 40; i++) {
    const v = Math.round((i * 255) / 39);
    cols.push(`#${hex(v)}${hex(v)}${hex(v)}`);
  }
  return cols;
})();

function UnitPicker({ units, unitIds, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-3" data-testid={`${testIdPrefix}-units`}>
      {units.map((u) => (
        <label key={u.id} className="flex items-center gap-1.5 text-xs text-slate-300">
          <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={unitIds.includes(u.id)}
            data-testid={`${testIdPrefix}-unit-${u.id}`}
            onChange={() => onChange(unitIds.includes(u.id) ? unitIds.filter((x) => x !== u.id) : [...unitIds, u.id])} />
          {u.name}
        </label>
      ))}
      {units.length === 0 && <span className="text-xs text-slate-600">No org units — applies company-wide.</span>}
      {units.length > 0 && <span className="text-[10px] text-slate-600">No selection = all units</span>}
    </div>
  );
}

function ScoreMatrix({ roles, scores, onChange, testIdPrefix }) {
  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-scores`}>
      <div className="grid grid-cols-[1fr,90px,90px] gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
        <span>Role</span><span>Risk score</span><span>Complexity</span>
      </div>
      {roles.map((r) => {
        const sc = scores[r.id] || { risk: 0, complexity: 0 };
        return (
          <div key={r.id} className="grid grid-cols-[1fr,90px,90px] gap-2 items-center">
            <span className="text-xs text-slate-300 truncate">{r.name}</span>
            <input type="number" step="0.5" value={sc.risk} data-testid={`${testIdPrefix}-risk-${r.id}`}
              onChange={(e) => onChange(r.id, { ...sc, risk: e.target.value })}
              className="px-2 py-1.5 rounded bg-slate-950 border border-slate-800 text-xs" />
            <input type="number" step="0.5" value={sc.complexity} data-testid={`${testIdPrefix}-cx-${r.id}`}
              onChange={(e) => onChange(r.id, { ...sc, complexity: e.target.value })}
              className="px-2 py-1.5 rounded bg-slate-950 border border-slate-800 text-xs" />
          </div>
        );
      })}
    </div>
  );
}

export default function TaskConfigTab() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [roles, setRoles] = useState([]);
  const [units, setUnits] = useState([]);
  const [types, setTypes] = useState([]);
  const [cfg, setCfg] = useState({ modes: [], sequence_groups: [], sequences: [] });
  const [tForm, setTForm] = useState(emptyType);
  const [taskSettings, setTaskSettings] = useState(null);
  const [equipTypes, setEquipTypes] = useState([]);
  const [tEditId, setTEditId] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const seedDemo = async () => {
    if (!window.confirm("This DELETES all existing tasks and commitments for this company and replaces them with seeded demo data. Continue?")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-demo", { company_id: companyId });
      toast.success(`Seeded ${data.tasks} tasks + ${data.commitments} commitments (cleared ${data.cleared_tasks + data.cleared_commitments} old)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSeeding(false); }
  };
  const [modeFor, setModeFor] = useState(undefined); // undefined=closed, null=new, object=edit
  const [groupFor, setGroupFor] = useState(undefined);
  const [seqFor, setSeqFor] = useState(undefined);
  const [copyOpen, setCopyOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [typeImportOpen, setTypeImportOpen] = useState(false);
  const [exportRole, setExportRole] = useState("");
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    api.get("/companies").then((r) => { setCompanies(r.data); if (r.data[0]) setCompanyId((p) => p || r.data[0].id); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTypes = () => api.get(`/task-types?company_id=${companyId}`).then((r) => setTypes(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  const loadCfg = () => api.get(`/task-config?company_id=${companyId}`).then((r) => setCfg(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  const exportConfig = async (fmt) => {
    try {
      const res = await api.get("/admin/task-config/export", { params: { company_id: companyId, fmt, ...(exportRole ? { role_id: exportRole } : {}) }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFilename("crew-ready-task-config", fmt, user);
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const exportTypes = async (fmt) => {
    try {
      const res = await api.get("/admin/task-types/export", { params: { company_id: companyId, fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFilename("crew-ready-task-types", fmt, user);
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  useEffect(() => {
    if (!companyId) return;
    api.get(`/roles?company_id=${companyId}`).then((r) => setRoles(r.data)).catch(() => {});
    api.get(`/org/tree?company_id=${companyId}`).then((r) => setUnits(r.data.units || [])).catch(() => {});
    api.get(`/companies/${companyId}/task-settings`).then((r) => setTaskSettings(r.data)).catch(() => setTaskSettings(null));
    api.get(`/equipment-config?company_id=${companyId}`).then((r) => setEquipTypes([...new Set((r.data.equipment || []).map((e) => e.type).filter(Boolean))])).catch(() => {});
    loadTypes();
    loadCfg();
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const unitName = (id) => units.find((u) => u.id === id)?.name || "?";
  const unitLabel = (ids) => (ids?.length ? ids.map(unitName).join(", ") : "All units");
  const toggleTRole = (id) => setTForm((f) => ({ ...f, role_ids: f.role_ids.includes(id) ? f.role_ids.filter((x) => x !== id) : [...f.role_ids, id] }));

  const submitType = async () => {
    if (!tForm.name.trim()) { toast.error("Name required"); return; }
    const payload = { company_id: companyId, name: tForm.name.trim(), kind: tForm.kind, brief_min: Number(tForm.brief_min) || 0, debrief_min: Number(tForm.debrief_min) || 0, role_ids: tForm.role_ids, color: tForm.kind === "commitment" ? tForm.color : null, fixed: tForm.kind === "commitment" ? !!tForm.fixed : false, requires_authorisation: tForm.kind === "task" ? !!tForm.requires_authorisation : false, auth_scope: tForm.auth_scope || "unit",
      airspaces: tForm.kind === "task" ? (tForm.airspaces_text || "").split(",").map((s) => s.trim()).filter(Boolean) : [],
      equipment_types: tForm.kind === "task" ? (tForm.equipment_types || []) : [] };
    try {
      if (tEditId) await api.put(`/admin/task-types/${tEditId}`, payload);
      else await api.post("/admin/task-types", payload);
      toast.success(`${tForm.kind === "commitment" ? "Commitment" : "Task"} type ${tEditId ? "updated" : "added"}`);
      setTForm(emptyType);
      setTEditId(null);
      loadTypes();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveTaskSettings = async () => {
    try {
      await api.put(`/admin/companies/${companyId}/task-settings`, {
        post_task_edit_days: Number(taskSettings.post_task_edit_days) || 0,
        post_task_history_days: Number(taskSettings.post_task_history_days) || 14,
      });
      toast.success("Post-task settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delType = async (t) => {
    if (!window.confirm(`Delete ${t.kind === "commitment" ? "commitment" : "task"} type "${t.name}"? Already-logged entries keep their name.`)) return;
    await api.delete(`/admin/task-types/${t.id}`);
    toast.success(`${t.kind === "commitment" ? "Commitment" : "Task"} type deleted`);
    loadTypes();
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-5 rounded-lg border border-slate-800 bg-slate-900/60">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="taskcfg-company"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-3 flex items-end justify-end">
          <button onClick={() => setCopyOpen(true)} data-testid="taskcfg-copy-open"
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs">
            <Copy className="h-3.5 w-3.5" /> Copy config to org units
          </button>
        </div>
      </div>

      {taskSettings && (
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="task-settings-card">
          <div className="font-heading text-lg font-semibold mb-1">Post-task settings</div>
          <p className="text-xs text-slate-500 mb-4">How long a completed task stays editable before a supervisor unlock is needed, and how far back the Post Task page lists completed tasks.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Edit window (days after completion)</label>
              <input type="number" min="0" max="365" value={taskSettings.post_task_edit_days}
                onChange={(e) => setTaskSettings({ ...taskSettings, post_task_edit_days: e.target.value })}
                data-testid="task-settings-edit-days" className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Completed history (days shown)</label>
              <input type="number" min="1" max="365" value={taskSettings.post_task_history_days}
                onChange={(e) => setTaskSettings({ ...taskSettings, post_task_history_days: e.target.value })}
                data-testid="task-settings-history-days" className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
            </div>
          </div>
          <button onClick={saveTaskSettings} data-testid="task-settings-save"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors">
            Save post-task settings
          </button>
        </div>
      )}

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="task-types-card">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <div className="font-heading text-lg font-semibold">Task & Commitment Types</div>
          <div className="flex items-center gap-2">
            <button onClick={() => exportTypes("csv")} data-testid="task-types-export"
              title="Download task types as CSV"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs">
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
            <button onClick={() => exportTypes("xlsx")} data-testid="task-types-export-xlsx"
              title="Download task types as XLSX"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs">
              <Download className="h-3.5 w-3.5" /> Export XLSX
            </button>
            <button onClick={() => setTypeImportOpen(true)} data-testid="task-types-import-open"
              title="Import task types from CSV"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs">
              <Upload className="h-3.5 w-3.5" /> Import CSV
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-4">Brief/debrief defaults auto-fill when a member logs the task. Restrict to roles, or leave all unticked for every role.</p>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Name</label>
            <input value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} data-testid="task-type-name"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" placeholder="e.g. Sortie, Commitment" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Kind</label>
            <select value={tForm.kind} onChange={(e) => setTForm({ ...tForm, kind: e.target.value })} data-testid="task-type-kind"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
              <option value="task">Task</option>
              <option value="commitment">Commitment</option>
            </select>
          </div>
          {tForm.kind === "commitment" && (
            <div className="relative">
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Colour</label>
              <button type="button" onClick={() => setPaletteOpen((o) => !o)} data-testid="task-type-color"
                className="w-full h-[42px] rounded-md border border-slate-800 bg-slate-950 flex items-center gap-2 px-2.5 hover:border-slate-600 transition-colors">
                <span className="h-6 w-6 rounded-sm border border-slate-700" style={{ background: tForm.color }} />
                <span className="text-xs font-mono-num text-slate-400">{tForm.color}</span>
              </button>
              {paletteOpen && (
                <div className="absolute z-20 mt-1 p-2 rounded-md border border-slate-700 bg-slate-950 shadow-xl" data-testid="color-palette"
                  style={{ display: "grid", gridTemplateColumns: "repeat(16, 1fr)", gap: 2 }}>
                  {PALETTE256.map((c) => (
                    <button key={c} type="button" title={c} data-testid={`color-swatch-${c}`}
                      onClick={() => { setTForm({ ...tForm, color: c }); setPaletteOpen(false); }}
                      className={`h-4 w-4 rounded-[3px] transition-transform hover:scale-125 ${tForm.color === c ? "ring-2 ring-white" : ""}`}
                      style={{ background: c }} />
                  ))}
                </div>
              )}
            </div>
          )}
          {tForm.kind === "commitment" && (
            <div className="flex items-end pb-2.5">
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={tForm.fixed} data-testid="task-type-fixed"
                  onChange={(e) => setTForm({ ...tForm, fixed: e.target.checked })} />
                Fixed (not moveable)
              </label>
            </div>
          )}
          {tForm.kind === "task" && (
            <div className="flex items-end pb-2.5">
              <label className="flex items-center gap-2 text-xs text-slate-300" title="An Auth Officer must authorise committed tasks of this type before post-task completion">
                <input type="checkbox" className="accent-violet-600 h-4 w-4" checked={!!tForm.requires_authorisation} data-testid="task-type-requires-auth"
                  onChange={(e) => setTForm({ ...tForm, requires_authorisation: e.target.checked })} />
                Requires authorisation
              </label>
            </div>
          )}
          {tForm.kind === "task" && tForm.requires_authorisation && (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Authorisation scope</label>
              <select value={tForm.auth_scope || "unit"} onChange={(e) => setTForm({ ...tForm, auth_scope: e.target.value })} data-testid="task-type-auth-scope"
                title="Which Auth Officers can authorise tasks of this type"
                className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
                <option value="team">Same team</option>
                <option value="subunit">Same subunit</option>
                <option value="unit">Same unit</option>
                <option value="company">Anywhere in company</option>
              </select>
            </div>
          )}
          {tForm.kind === "task" && (
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Airspace options (comma-separated — picked on the task's Locations tab)</label>
              <input value={tForm.airspaces_text || ""} onChange={(e) => setTForm({ ...tForm, airspaces_text: e.target.value })} data-testid="task-type-airspaces"
                className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" placeholder="e.g. Restricted, Danger Area, Training Area A" />
            </div>
          )}
          {tForm.kind === "task" && equipTypes.length > 0 && (
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Equipment types used (multi-select — filters the picker when building a task)</label>
              <div className="flex flex-wrap gap-2" data-testid="task-type-equip-types">
                {equipTypes.map((et) => {
                  const on = (tForm.equipment_types || []).includes(et);
                  return (
                    <button key={et} type="button" data-testid={`task-type-equiptype-${et}`}
                      onClick={() => setTForm({ ...tForm, equipment_types: on ? tForm.equipment_types.filter((x) => x !== et) : [...(tForm.equipment_types || []), et] })}
                      className={`px-2.5 py-1 rounded text-xs border transition-colors ${on ? "bg-emerald-600/20 text-emerald-300 border-emerald-600/40" : "bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-600"}`}>
                      {et}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Brief (min)</label>
            <input type="number" min="0" value={tForm.brief_min} onChange={(e) => setTForm({ ...tForm, brief_min: e.target.value })}
              data-testid="task-type-brief" className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Debrief (min)</label>
            <input type="number" min="0" value={tForm.debrief_min} onChange={(e) => setTForm({ ...tForm, debrief_min: e.target.value })}
              data-testid="task-type-debrief" className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
          </div>
          <div className="flex items-end">
            <button onClick={submitType} data-testid="task-type-save"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm">
              <Plus className="h-4 w-4" /> {tEditId ? "Save" : "Add"}
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-3">
          {roles.map((r) => (
            <label key={r.id} className="flex items-center gap-1.5 text-xs text-slate-300">
              <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={tForm.role_ids.includes(r.id)}
                data-testid={`task-type-role-${r.id}`} onChange={() => toggleTRole(r.id)} />
              {r.name}
            </label>
          ))}
        </div>
        {tEditId && (
          <button onClick={() => { setTEditId(null); setTForm(emptyType); }} data-testid="task-type-cancel"
            className="mt-2 text-xs text-slate-400 hover:text-white">Cancel edit</button>
        )}
        <div className="divide-y divide-slate-800 mt-4">
          {types.length === 0 && <div className="py-3 text-sm text-slate-500">No task types yet.</div>}
          {types.map((t) => (
            <div key={t.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`task-type-row-${t.id}`}>
              <div className="text-sm">
                {t.name}
                {t.kind === "commitment" && <span className="ml-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest border" style={t.color ? { color: t.color, borderColor: t.color, background: `${t.color}1f` } : undefined} data-testid={`task-type-swatch-${t.id}`}>Commitment</span>}
                {t.kind !== "commitment" && t.requires_authorisation && <span className="ml-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest border bg-violet-600/15 text-violet-300 border-violet-600/40" data-testid={`task-type-reqauth-${t.id}`}>Auth req'd</span>}
                {t.fixed && <span className="ml-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest bg-slate-700/40 text-slate-300 border border-slate-600/40">Fixed</span>}
                <span className="ml-2 text-[10px] font-mono-num text-slate-500">+{t.brief_min}m / +{t.debrief_min}m</span>
                <span className="ml-2 text-[10px] text-slate-500">{t.role_ids?.length ? t.role_ids.map((id) => roles.find((r) => r.id === id)?.name || "?").join(", ") : "All roles"}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => { setTEditId(t.id); setTForm({ name: t.name, kind: t.kind || "task", brief_min: t.brief_min ?? 0, debrief_min: t.debrief_min ?? 0, role_ids: t.role_ids || [], color: t.color || "#8b5cf6", fixed: !!t.fixed, requires_authorisation: !!t.requires_authorisation, auth_scope: t.auth_scope || "unit", airspaces_text: (t.airspaces || []).join(", "), equipment_types: t.equipment_types || [] }); }}
                  data-testid={`task-type-edit-${t.id}`} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1">
                  <Pencil className="h-3 w-3" /> Edit
                </button>
                <button onClick={() => delType(t)} data-testid={`task-type-del-${t.id}`}
                  className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="modes-card">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <div className="font-heading text-lg font-semibold">Modes</div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={exportRole} onChange={(e) => setExportRole(e.target.value)} data-testid="task-config-export-role"
              title="Include risk & complexity columns for this role in the export"
              className="px-2.5 py-2 rounded-md bg-slate-950 border border-slate-800 text-xs text-slate-300">
              <option value="">Names only</option>
              {roles.map((r) => <option key={r.id} value={r.id}>Scores: {r.name}</option>)}
            </select>
            <button onClick={() => exportConfig("csv")} data-testid="task-config-export"
              title="Download modes, sequence groups and sequences as CSV"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs">
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
            <button onClick={() => exportConfig("xlsx")} data-testid="task-config-export-xlsx"
              title="Download modes, sequence groups and sequences as XLSX"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs">
              <Download className="h-3.5 w-3.5" /> Export XLSX
            </button>
            <button onClick={() => setImportOpen(true)} data-testid="task-config-import-open"
              title="Import modes, sequence groups and sequences from CSV"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs">
              <Upload className="h-3.5 w-3.5" /> Import CSV
            </button>
            <button onClick={() => setModeFor(null)} data-testid="mode-add-open"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs">
              <Plus className="h-3.5 w-3.5" /> Add mode
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-3">Multi-select options on each task. Risk score and complexity factor are set per role.</p>
        <div className="divide-y divide-slate-800">
          {cfg.modes.length === 0 && <div className="py-3 text-sm text-slate-500">No modes yet.</div>}
          {cfg.modes.map((m) => (
            <div key={m.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`mode-row-${m.id}`}>
              <div className="text-sm">
                {m.name}
                <span className="ml-2 text-[10px] text-slate-500">{unitLabel(m.unit_ids)}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => setModeFor(m)} data-testid={`mode-edit-${m.id}`}
                  className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
                <button onClick={async () => { if (window.confirm(`Delete mode "${m.name}"?`)) { await api.delete(`/admin/task-modes/${m.id}`); loadCfg(); toast.success("Mode deleted"); } }}
                  data-testid={`mode-del-${m.id}`} className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="sequences-card">
        <div className="flex items-center justify-between mb-1">
          <div className="font-heading text-lg font-semibold">Sequences</div>
          <button onClick={() => setGroupFor(null)} data-testid="seq-group-add-open"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs">
            <Plus className="h-3.5 w-3.5" /> Add sequence group
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-3">Grouped sequences — the group header selects/deselects all its sequences on the task form.</p>
        {cfg.sequence_groups.length === 0 && <div className="py-3 text-sm text-slate-500">No sequence groups yet.</div>}
        <div className="space-y-2">
          {cfg.sequence_groups.map((g) => {
            const seqs = cfg.sequences.filter((s) => s.group_id === g.id);
            const open = openGroups[g.id] !== false;
            return (
              <div key={g.id} className="rounded-md border border-slate-800 bg-slate-950/40" data-testid={`seq-group-row-${g.id}`}>
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <button onClick={() => setOpenGroups((o) => ({ ...o, [g.id]: !open }))} data-testid={`seq-group-toggle-${g.id}`}
                    className="text-slate-400 hover:text-white">
                    {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <span className="text-sm font-medium">{g.name}</span>
                  <span className="text-[10px] text-slate-500">{unitLabel(g.unit_ids)}</span>
                  <div className="ml-auto flex items-center gap-3">
                    <button onClick={() => setSeqFor({ group_id: g.id })} data-testid={`seq-add-${g.id}`}
                      className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Sequence</button>
                    <button onClick={() => setGroupFor(g)} data-testid={`seq-group-edit-${g.id}`}
                      className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
                    <button onClick={async () => { if (window.confirm(`Delete group "${g.name}" and its sequences?`)) { await api.delete(`/admin/task-sequence-groups/${g.id}`); loadCfg(); toast.success("Group deleted"); } }}
                      data-testid={`seq-group-del-${g.id}`} className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /></button>
                  </div>
                </div>
                {open && (
                  <div className="border-t border-slate-800/70 divide-y divide-slate-800/50">
                    {seqs.length === 0 && <div className="px-8 py-2 text-xs text-slate-600">No sequences in this group.</div>}
                    {seqs.map((s) => (
                      <div key={s.id} className="px-8 py-2 flex items-center justify-between gap-3" data-testid={`seq-row-${s.id}`}>
                        <div className="text-xs text-slate-300">
                          {s.name}
                          <span className="ml-2 text-[10px] text-slate-500">{unitLabel(s.unit_ids)}</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <button onClick={() => setSeqFor(s)} data-testid={`seq-edit-${s.id}`}
                            className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
                          <button onClick={async () => { if (window.confirm(`Delete sequence "${s.name}"?`)) { await api.delete(`/admin/task-sequences/${s.id}`); loadCfg(); toast.success("Sequence deleted"); } }}
                            data-testid={`seq-del-${s.id}`} className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="demo-data-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-heading font-semibold text-sm">Demo data</h3>
            <p className="text-xs text-slate-500 mt-1">Replaces ALL tasks and commitments for this company with a seeded demo week (5 days back through 6 ahead). Configuration is untouched.</p>
          </div>
          <button onClick={seedDemo} disabled={seeding || !companyId} data-testid="seed-demo-btn"
            className="px-3 py-2 rounded-md bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 border border-amber-600/40 text-xs font-medium transition-colors disabled:opacity-40">
            {seeding ? "Seeding…" : "Seed demo data"}
          </button>
        </div>
      </div>

      <ModeDialog mode={modeFor} companyId={companyId} roles={roles} units={units}
        onClose={() => setModeFor(undefined)} onDone={() => { setModeFor(undefined); loadCfg(); }} />
      <GroupDialog group={groupFor} companyId={companyId} units={units}
        onClose={() => setGroupFor(undefined)} onDone={() => { setGroupFor(undefined); loadCfg(); }} />
      <SeqDialog seq={seqFor} companyId={companyId} roles={roles} units={units}
        onClose={() => setSeqFor(undefined)} onDone={() => { setSeqFor(undefined); loadCfg(); }} />
      <CopyDialog open={copyOpen} companyId={companyId} units={units} cfg={cfg}
        onClose={() => setCopyOpen(false)} onDone={() => { setCopyOpen(false); loadCfg(); }} />
      <ImportDialog open={importOpen} companyId={companyId} roles={roles}
        onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); loadCfg(); }} />
      <TypeImportDialog open={typeImportOpen} companyId={companyId}
        onClose={() => setTypeImportOpen(false)} onDone={() => { setTypeImportOpen(false); loadTypes(); }} />
    </div>
  );
}

function ModeDialog({ mode, companyId, roles, units, onClose, onDone }) {
  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [unitIds, setUnitIds] = useState([]);
  const [scores, setScores] = useState({});
  useEffect(() => {
    if (mode === undefined) return;
    setName(mode?.name || "");
    setShortName(mode?.short_name || "");
    setUnitIds(mode?.unit_ids || []);
    setScores(mode?.scores || {});
  }, [mode]);
  if (mode === undefined) return null;
  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    const payload = { company_id: companyId, name: name.trim(), short_name: shortName.trim() || null, unit_ids: unitIds, scores };
    try {
      if (mode?.id) await api.put(`/admin/task-modes/${mode.id}`, payload);
      else await api.post("/admin/task-modes", payload);
      toast.success("Mode saved");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="mode-dialog">
        <DialogHeader><DialogTitle className="font-heading">{mode?.id ? "Edit mode" : "New mode"}</DialogTitle></DialogHeader>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="mode-name"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Short form (used in exports)</label>
        <input value={shortName} onChange={(e) => setShortName(e.target.value)} data-testid="mode-short-name" placeholder="Optional abbreviation"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Available to org units</label>
        <UnitPicker units={units} unitIds={unitIds} onChange={setUnitIds} testIdPrefix="mode" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mt-4 mb-1.5">Risk & complexity per role</label>
        <ScoreMatrix roles={roles} scores={scores} testIdPrefix="mode"
          onChange={(rid, sc) => setScores((s) => ({ ...s, [rid]: sc }))} />
        <button onClick={submit} data-testid="mode-save"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save mode</button>
      </DialogContent>
    </Dialog>
  );
}

function GroupDialog({ group, companyId, units, onClose, onDone }) {
  const [name, setName] = useState("");
  const [unitIds, setUnitIds] = useState([]);
  useEffect(() => {
    if (group === undefined) return;
    setName(group?.name || "");
    setUnitIds(group?.unit_ids || []);
  }, [group]);
  if (group === undefined) return null;
  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    const payload = { company_id: companyId, name: name.trim(), unit_ids: unitIds };
    try {
      if (group?.id) await api.put(`/admin/task-sequence-groups/${group.id}`, payload);
      else await api.post("/admin/task-sequence-groups", payload);
      toast.success("Sequence group saved");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="seq-group-dialog">
        <DialogHeader><DialogTitle className="font-heading">{group?.id ? "Edit sequence group" : "New sequence group"}</DialogTitle></DialogHeader>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Header name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="seq-group-name"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Available to org units</label>
        <UnitPicker units={units} unitIds={unitIds} onChange={setUnitIds} testIdPrefix="seq-group" />
        <button onClick={submit} data-testid="seq-group-save"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save group</button>
      </DialogContent>
    </Dialog>
  );
}

function SeqDialog({ seq, companyId, roles, units, onClose, onDone }) {
  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [unitIds, setUnitIds] = useState([]);
  const [scores, setScores] = useState({});
  useEffect(() => {
    if (seq === undefined) return;
    setName(seq?.name || "");
    setShortName(seq?.short_name || "");
    setUnitIds(seq?.unit_ids || []);
    setScores(seq?.scores || {});
  }, [seq]);
  if (seq === undefined) return null;
  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    const payload = { company_id: companyId, group_id: seq.group_id, name: name.trim(), short_name: shortName.trim() || null, unit_ids: unitIds, scores };
    try {
      if (seq?.id) await api.put(`/admin/task-sequences/${seq.id}`, payload);
      else await api.post("/admin/task-sequences", payload);
      toast.success("Sequence saved");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="seq-dialog">
        <DialogHeader><DialogTitle className="font-heading">{seq?.id ? "Edit sequence" : "New sequence"}</DialogTitle></DialogHeader>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="seq-name"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Short form (used in exports)</label>
        <input value={shortName} onChange={(e) => setShortName(e.target.value)} data-testid="seq-short-name" placeholder="Optional abbreviation"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Available to org units</label>
        <UnitPicker units={units} unitIds={unitIds} onChange={setUnitIds} testIdPrefix="seq" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mt-4 mb-1.5">Risk & complexity per role</label>
        <ScoreMatrix roles={roles} scores={scores} testIdPrefix="seq"
          onChange={(rid, sc) => setScores((s) => ({ ...s, [rid]: sc }))} />
        <button onClick={submit} data-testid="seq-save"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save sequence</button>
      </DialogContent>
    </Dialog>
  );
}

function CopyDialog({ open, companyId, units, cfg, onClose, onDone }) {
  const [targets, setTargets] = useState([]);
  useEffect(() => { if (open) setTargets([]); }, [open]);
  if (!open) return null;
  const submit = async () => {
    if (targets.length === 0) { toast.error("Select at least one target org unit"); return; }
    try {
      const { data } = await api.post("/admin/task-config/copy", {
        company_id: companyId, target_unit_ids: targets,
        mode_ids: (cfg?.modes || []).map((m) => m.id),
        group_ids: (cfg?.sequence_groups || []).map((g) => g.id),
      });
      toast.success(`Copied ${data.copied} item(s)${data.skipped_duplicates ? `, skipped ${data.skipped_duplicates} existing duplicate(s)` : ""} — scoped to the selected units`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="task-copy-dialog">
        <DialogHeader><DialogTitle className="font-heading">Copy task config to org units</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 mb-3">Clones every mode and every sequence group (with its sequences), available only to the selected org units.</p>
        <div className="space-y-1.5">
          {units.map((u) => (
            <label key={u.id} className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={targets.includes(u.id)}
                data-testid={`copy-target-${u.id}`}
                onChange={() => setTargets((t) => t.includes(u.id) ? t.filter((x) => x !== u.id) : [...t, u.id])} />
              {u.name}
            </label>
          ))}
          {units.length === 0 && <div className="text-xs text-slate-600">No org units defined for this company.</div>}
        </div>
        <button onClick={submit} data-testid="task-copy-confirm"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Copy to selected units</button>
      </DialogContent>
    </Dialog>
  );
}

function TypeImportDialog({ open, companyId, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setText(""); }, [open]);
  if (!open) return null;
  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setText(String(r.result || ""));
    r.readAsText(f);
    e.target.value = "";
  };
  const submit = async () => {
    if (!text.trim()) { toast.error("Paste CSV content or pick a file first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/task-types/import", { company_id: companyId, text });
      toast.success(`Imported ${data.added} task type(s)`
        + (data.skipped_duplicates ? ` — skipped ${data.skipped_duplicates} duplicate(s)` : "")
        + (data.skipped_invalid ? ` — ${data.skipped_invalid} invalid row(s)` : ""));
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="task-types-import-dialog">
        <DialogHeader><DialogTitle className="font-heading">Import task types (CSV)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 mb-1">Columns: <span className="font-mono-num">Task Type, Kind, Fixed, Brief (min), Debrief (min), Color</span> — same layout as the task types export.</p>
        <ul className="text-xs text-slate-500 mb-3 list-disc pl-4 space-y-0.5">
          <li>Kind is <span className="font-mono-num">task</span> or <span className="font-mono-num">commitment</span> (defaults to task).</li>
          <li>Fixed and Color apply to commitments only.</li>
          <li>Rows matching an existing task type name are skipped as duplicates.</li>
          <li>Role restrictions are not imported — edit the type after import to add them.</li>
        </ul>
        <input type="file" accept=".csv,text/csv,text/plain" onChange={pickFile} data-testid="task-types-import-file"
          className="block w-full text-xs text-slate-400 mb-2 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700 file:cursor-pointer" />
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} data-testid="task-types-import-text"
          placeholder={"Task Type,Kind,Fixed,Brief (min),Debrief (min),Color\nSortie,task,No,15,10,\nDNIF,commitment,Yes,0,0,#8b5cf6"}
          className="w-full px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-xs font-mono-num" />
        <button onClick={submit} disabled={busy} data-testid="task-types-import-submit"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-60">
          <Upload className="h-4 w-4" /> {busy ? "Importing…" : "Import"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, companyId, roles, onClose, onDone }) {
  const [text, setText] = useState("");
  const [scoreRole, setScoreRole] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setText(""); setScoreRole(""); } }, [open]);
  if (!open) return null;
  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setText(String(r.result || ""));
    r.readAsText(f);
    e.target.value = "";
  };
  const submit = async () => {
    if (!text.trim()) { toast.error("Paste CSV content or pick a file first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/task-config/import", { company_id: companyId, text, role_id: scoreRole || null });
      toast.success(`Imported ${data.modes} mode(s), ${data.groups} group(s), ${data.sequences} sequence(s)`
        + (data.skipped_duplicates ? ` — skipped ${data.skipped_duplicates} duplicate(s)` : "")
        + (data.skipped_invalid ? ` — ${data.skipped_invalid} invalid row(s)` : ""));
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="task-config-import-dialog">
        <DialogHeader><DialogTitle className="font-heading">Import modes & sequences (CSV)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 mb-1">Columns: <span className="font-mono-num">Mode, Sequence Group, Sequence</span> (optional 4th/5th: <span className="font-mono-num">Risk, Complexity</span>).</p>
        <ul className="text-xs text-slate-500 mb-3 list-disc pl-4 space-y-0.5">
          <li>A row with only a Mode adds a new mode.</li>
          <li>A row with a blank Mode adds the Sequence to the Sequence Group (the group is created if missing).</li>
          <li>Rows matching existing names are skipped as duplicates.</li>
          <li>Risk &amp; Complexity values are stored for the role chosen below.</li>
        </ul>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Apply risk &amp; complexity to role (optional)</label>
        <select value={scoreRole} onChange={(e) => setScoreRole(e.target.value)} data-testid="task-config-import-role"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3">
          <option value="">None — ignore score columns</option>
          {(roles || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <input type="file" accept=".csv,text/csv,text/plain" onChange={pickFile} data-testid="task-config-import-file"
          className="block w-full text-xs text-slate-400 mb-2 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700 file:cursor-pointer" />
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} data-testid="task-config-import-text"
          placeholder={"Mode,Sequence Group,Sequence\nNVG,,\n,Engine Runs,Run 1"}
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm font-mono-num" />
        <button onClick={submit} disabled={busy} data-testid="task-config-import-btn"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <Upload className="h-4 w-4" /> {busy ? "Importing…" : "Import"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
